import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faCalendarAlt,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import CountUp from 'react-countup';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';

registerLocale('es', es);

echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const NOMBRES_MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const getNombreMes = (mesNum) => {
    if (!mesNum || mesNum < 1 || mesNum > 12) return '';
    return NOMBRES_MESES[mesNum - 1];
};

/**
 * Card de Gráfica y Órdenes Mensuales con diseño réplica de TimeMetricCard (Imagen 1):
 * - Borde izquierdo con acento púrpura
 * - Gran número principal del mes actual con indicador
 * - Bloques interactivos coloreados para mes pasado y antepasado
 * - Gráfica ECharts con curva suave, gradiente de área y líneas punteadas de referencia
 * - Selector interactivo de series y rango de 3 / 6 meses
 * - Adaptación total a modo Claro y Oscuro mediante clases de Falcon Bootstrap
 */
const DistribuidorGraficaCard = ({
    resumen = {},
    historico = [],
    cargando = false,
    mesesRango = 6,
    setMesesRango,
    selectedDate = new Date(),
    onDateChange
}) => {
    const context = useContext(AppContext);
    const isDarkMode = Boolean(
        context?.config?.isDark ||
        context?.isDark ||
        (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) ||
        (typeof window !== 'undefined' && (localStorage.getItem('isDark') === 'true' || localStorage.getItem('theme') === 'dark'))
    );

    const selectedDateObj = selectedDate || new Date();

    const hoy = new Date();
    const isCurrentMonth =
        selectedDateObj.getFullYear() === hoy.getFullYear() &&
        selectedDateObj.getMonth() === hoy.getMonth();

    const handlePrevMonth = () => {
        const prev = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth() - 1, 1);
        if (onDateChange) {
            onDateChange(prev);
        }
    };

    const handleNextMonth = () => {
        const next = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth() + 1, 1);
        const hoyInicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        if (next <= hoyInicioMes && onDateChange) {
            onDateChange(next);
        }
    };

    const handleDateSelect = (date) => {
        if (onDateChange && date) {
            const nuevoMes = new Date(date.getFullYear(), date.getMonth(), 1);
            onDateChange(nuevoMes);
        }
    };

    const etiquetaPeriodoPrincipal = isCurrentMonth ? 'Mes Actual' : 'Mes Seleccionado';

    // Estados para visibilidad de series en la gráfica (como en Imagen 1)
    const [visibleSeries, setVisibleSeries] = useState({
        mesActual: true,
        mesPasado: true,
        mesAntepasado: true
    });

    const toggleSeries = (key) => {
        setVisibleSeries((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const activarTodas = () => {
        setVisibleSeries({
            mesActual: true,
            mesPasado: true,
            mesAntepasado: true
        });
    };

    // Identificar la información de los 3 meses a partir del histórico
    const mesActualInfo = historico.find((h) => h.iOrden === 6);
    const mesAnteriorInfo = historico.find((h) => h.iOrden === 5);
    const mesAntepasadoInfo = historico.find((h) => h.iOrden === 4);

    const etiquetaMesActual = mesActualInfo ? `${getNombreMes(mesActualInfo.iMes)} ${mesActualInfo.iAnio}` : 'Mes Actual';
    const etiquetaMesAnterior = mesAnteriorInfo ? `${getNombreMes(mesAnteriorInfo.iMes)} ${mesAnteriorInfo.iAnio}` : 'Mes Anterior';
    const etiquetaMesAntepasado = mesAntepasadoInfo ? `${getNombreMes(mesAntepasadoInfo.iMes)} ${mesAntepasadoInfo.iAnio}` : 'Mes Antepasado';

    const ordenesActual = resumen.iOrdenesMesActual || 0;
    const ordenesPasado = resumen.iOrdenesMesAnterior || 0;
    const ordenesAntepasado = resumen.iOrdenesMesAntepasado || 0;

    // Filtrar histórico según rango (3 o 6 meses)
    const datosFiltrados = mesesRango === 3 ? historico.slice(-3) : historico;

    const categories = datosFiltrados.map((item) => {
        return `${getNombreMes(item.iMes).substring(0, 3)} ${item.iAnio}`;
    });
    const seriesValues = datosFiltrados.map((item) => item.iCantidadOrdenes ?? 0);

    const lineColor = '#6366f1'; // Morado corporativo de la Imagen 1

    // Construcción de series dinámicas
    const series = [];

    // Serie 1: Curva mensual con gradiente de área
    if (visibleSeries.mesActual) {
        series.push({
            name: 'Órdenes Pagadas',
            type: 'line',
            data: seriesValues,
            smooth: 0.35,
            symbol: 'circle',
            symbolSize: 6,
            showSymbol: true,
            itemStyle: { color: lineColor },
            lineStyle: { width: 2.5, color: lineColor },
            areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: `${lineColor}40` },
                    { offset: 1, color: `${lineColor}05` }
                ])
            }
        });
    }

    // Serie 2: Línea punteada de referencia Mes Pasado (Verde)
    if (visibleSeries.mesPasado && categories.length > 0) {
        series.push({
            name: `Mes Pasado (${ordenesPasado})`,
            type: 'line',
            data: categories.map(() => ordenesPasado),
            showSymbol: false,
            itemStyle: { color: '#10b981' },
            lineStyle: { width: 2, type: 'dashed', color: '#10b981' }
        });
    }

    // Serie 3: Línea punteada de referencia Mes Antepasado (Ámbar)
    if (visibleSeries.mesAntepasado && categories.length > 0) {
        series.push({
            name: `Mes Antepasado (${ordenesAntepasado})`,
            type: 'line',
            data: categories.map(() => ordenesAntepasado),
            showSymbol: false,
            itemStyle: { color: '#f59e0b' },
            lineStyle: { width: 2, type: 'dashed', color: '#f59e0b' }
        });
    }

    const chartOptions = {
        tooltip: {
            trigger: 'axis',
            backgroundColor: isDarkMode ? '#121e2d' : '#ffffff',
            borderColor: isDarkMode ? '#233348' : '#e2e8f0',
            borderWidth: 1,
            textStyle: { color: isDarkMode ? '#f8fafc' : '#0f172a' },
            padding: [8, 12],
            extraCssText: isDarkMode
                ? 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5); border-radius: 8px;'
                : 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12); border-radius: 8px;',
            formatter: (params) => {
                if (!params || !params.length) return '';
                const mes = params[0].name;
                let html = `<div style="font-size: 0.80rem; font-weight: 700; margin-bottom: 4px; color: ${isDarkMode ? '#f8fafc' : '#0f172a'};">Período: ${mes}</div>`;

                params.forEach((p) => {
                    html += `
                        <div style="font-size: 0.75rem; display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 2px; color: ${isDarkMode ? '#cbd5e1' : '#475569'};">
                            <span>${p.marker} ${p.seriesName}:</span>
                            <b style="color: ${isDarkMode ? '#f8fafc' : '#0f172a'};">${p.value} órdenes</b>
                        </div>
                    `;
                });
                return html;
            }
        },
        grid: {
            top: 20,
            right: 15,
            bottom: 25,
            left: 10,
            containLabel: true
        },
        xAxis: {
            type: 'category',
            data: categories,
            boundaryGap: false,
            axisLine: { lineStyle: { color: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0' } },
            axisTick: { show: false },
            axisLabel: {
                color: isDarkMode ? '#94a3b8' : '#64748b',
                fontSize: 10.5
            }
        },
        yAxis: {
            type: 'value',
            min: 0,
            max: Math.max(5, Math.ceil(Math.max(...seriesValues, ordenesPasado, ordenesAntepasado, 1) * 1.25)),
            minInterval: 1,
            splitLine: {
                lineStyle: {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
                    type: 'dashed'
                }
            },
            axisLabel: {
                color: isDarkMode ? '#94a3b8' : '#64748b',
                fontSize: 10.5
            }
        },
        series
    };



    return (
        <Card
            className="shadow-sm mb-3 position-relative overflow-hidden"
            style={{
                borderRadius: '12px',
                borderLeft: '4px solid #6366f1'
            }}
        >
            <Card.Body className="p-3">
                {/* Header: Label a la izquierda y Selector de fecha / filtros a la derecha */}
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm"
                            style={{ width: '28px', height: '28px', backgroundColor: '#6366f1', fontSize: '0.85rem' }}
                        >
                            <FontAwesomeIcon icon={faChartLine} />
                        </span>
                        <span
                            className="fw-bold mb-0 text-uppercase text-700"
                            style={{ fontSize: '0.80rem', letterSpacing: '0.5px' }}
                        >
                            CANTIDAD DE ÓRDENES DEL DISTRIBUIDOR
                        </span>
                    </div>

                    {/* Controles del lado derecho: Selector de fecha compacto y Rango */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Selector de Mes Interactivo con react-datepicker y Flechas Anterior / Siguiente */}
                        <div
                            className="d-flex align-items-center rounded-2 px-1 border border-200 bg-100"
                            style={{ height: '28px' }}
                        >
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-600"
                                onClick={handlePrevMonth}
                                title="Mes anterior"
                                style={{ width: '20px', height: '20px' }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.65rem' }} />
                            </Button>

                            <div className="d-flex align-items-center px-1">
                                <FontAwesomeIcon
                                    icon={faCalendarAlt}
                                    className="text-primary me-1"
                                    style={{ fontSize: '0.72rem' }}
                                />
                                <DatePicker
                                    selected={selectedDateObj}
                                    onChange={handleDateSelect}
                                    maxDate={new Date()}
                                    dateFormat="MMMM yyyy"
                                    showMonthYearPicker
                                    locale="es"
                                    className="form-control form-control-sm border-0 bg-transparent py-0 px-0 text-center fw-semibold text-1000 text-capitalize"
                                    style={{
                                        width: '110px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        color: isDarkMode ? '#e2e8f0' : '#1e293b'
                                    }}
                                />
                            </div>

                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-600"
                                onClick={handleNextMonth}
                                disabled={isCurrentMonth}
                                title="Mes siguiente"
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    opacity: isCurrentMonth ? 0.3 : 1,
                                    cursor: isCurrentMonth ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.65rem' }} />
                            </Button>
                        </div>

                        {/* Selector 3 / 6 meses */}
                        {setMesesRango && (
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 fw-semibold ${mesesRango === 3 ? 'btn-primary' : 'btn-falcon-default'}`}
                                    style={{
                                        fontSize: '0.74rem',
                                        height: '28px'
                                    }}
                                    onClick={() => setMesesRango(3)}
                                >
                                    3 meses
                                </button>
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 fw-semibold ${mesesRango === 6 ? 'btn-primary' : 'btn-falcon-default'}`}
                                    style={{
                                        fontSize: '0.74rem',
                                        height: '28px'
                                    }}
                                    onClick={() => setMesesRango(6)}
                                >
                                    6 meses
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Fila con Pills de Series: "Todas", "• Hoy / Mes Actual", "--- 3 días / Mes Pasado", "--- 7 días / Mes Antepasado" */}
                <div className="d-flex align-items-center justify-content-end gap-1 mb-2 flex-wrap">
                    <button
                        type="button"
                        onClick={activarTodas}
                        className="btn btn-sm btn-falcon-default rounded-pill px-2 py-0 fw-semibold"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px'
                        }}
                        title="Mostrar todas las series en la gráfica"
                    >
                        Todas
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('mesActual')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #6366f1',
                            backgroundColor: visibleSeries.mesActual ? '#6366f1' : 'transparent',
                            color: visibleSeries.mesActual ? '#ffffff' : '#6366f1',
                            opacity: visibleSeries.mesActual ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar visibilidad del mes actual"
                    >
                        <span>● {etiquetaPeriodoPrincipal}</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('mesPasado')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #10b981',
                            backgroundColor: visibleSeries.mesPasado ? '#10b981' : 'transparent',
                            color: visibleSeries.mesPasado ? '#ffffff' : '#10b981',
                            opacity: visibleSeries.mesPasado ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar línea de referencia del mes pasado"
                    >
                        <span>--- Mes Pasado</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('mesAntepasado')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #f59e0b',
                            backgroundColor: visibleSeries.mesAntepasado ? '#f59e0b' : 'transparent',
                            color: visibleSeries.mesAntepasado ? '#ffffff' : '#f59e0b',
                            opacity: visibleSeries.mesAntepasado ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar línea de referencia del mes antepasado"
                    >
                        <span>--- Mes Antepasado</span>
                    </button>
                </div>

                {/* Contenido: Bloque métricas a la izquierda + Gráfica a la derecha */}
                <div className="row g-2 align-items-center mt-1">
                    {/* Columna Izquierda: Número grande Mes Actual + Mes Pasado y Antepasado interactivos */}
                    <div className="col-12 col-md-5 col-lg-4">
                        <div className="d-flex flex-column justify-content-center pe-md-2">
                            {/* Número grande: Mes Actual */}
                            <div
                                role="button"
                                onClick={() => toggleSeries('mesActual')}
                                title="Clic para mostrar / ocultar en la gráfica"
                                style={{
                                    cursor: 'pointer',
                                    opacity: visibleSeries.mesActual ? 1 : 0.45,
                                    transition: 'opacity 0.2s'
                                }}
                            >
                                <div
                                    className="fw-extrabold mb-1 text-1000"
                                    style={{
                                        fontSize: '2.5rem',
                                        lineHeight: '1.1',
                                        fontWeight: 800
                                    }}
                                >
                                    {cargando ? (
                                        <Spinner animation="border" size="sm" variant="secondary" />
                                    ) : (
                                        <CountUp start={0} end={ordenesActual} duration={1.2} separator="," />
                                    )}
                                    <span className="text-600" style={{ fontSize: '1rem', fontWeight: 600, marginLeft: '6px' }}>
                                        órdenes
                                    </span>
                                </div>

                                {/* Subtítulo indicando que el número grande es el mes actual */}
                                <div
                                    className="d-flex align-items-center gap-1 mb-2 fw-semibold text-600"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: lineColor,
                                            display: 'inline-block'
                                        }}
                                    />
                                    <span>{etiquetaPeriodoPrincipal} ({etiquetaMesActual})</span>
                                    <span style={{ fontSize: '0.68rem', opacity: 0.75 }}>
                                        {visibleSeries.mesActual ? '(visible)' : '(oculto)'}
                                    </span>
                                </div>
                            </div>

                            {/* Bloques interactivos: Mes Pasado (Verde) y Mes Antepasado (Ámbar) */}
                            <div className="d-flex flex-column gap-1 pt-2 border-top border-200">
                                {/* Mes Pasado */}
                                <div
                                    role="button"
                                    onClick={() => toggleSeries('mesPasado')}
                                    title="Clic para mostrar / ocultar en la gráfica"
                                    className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                                    style={{
                                        fontSize: '0.78rem',
                                        cursor: 'pointer',
                                        opacity: visibleSeries.mesPasado ? 1 : 0.45,
                                        backgroundColor: visibleSeries.mesPasado
                                            ? 'rgba(16, 185, 129, 0.12)'
                                            : 'transparent',
                                        border: visibleSeries.mesPasado
                                            ? '1px solid rgba(16, 185, 129, 0.35)'
                                            : '1px solid transparent',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-1 text-800">
                                        <span style={{ width: 12, height: 2, borderTop: '2px dashed #10b981', display: 'inline-block' }} />
                                        <span>-- Mes pasado ({etiquetaMesAnterior})</span>
                                    </div>
                                    <span
                                        className="fw-bold ms-1"
                                        style={{
                                            color: visibleSeries.mesPasado ? '#10b981' : undefined
                                        }}
                                    >
                                        {ordenesPasado} órdenes
                                    </span>
                                </div>

                                {/* Mes Antepasado */}
                                <div
                                    role="button"
                                    onClick={() => toggleSeries('mesAntepasado')}
                                    title="Clic para mostrar / ocultar en la gráfica"
                                    className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                                    style={{
                                        fontSize: '0.78rem',
                                        cursor: 'pointer',
                                        opacity: visibleSeries.mesAntepasado ? 1 : 0.45,
                                        backgroundColor: visibleSeries.mesAntepasado
                                            ? 'rgba(245, 158, 11, 0.12)'
                                            : 'transparent',
                                        border: visibleSeries.mesAntepasado
                                            ? '1px solid rgba(245, 158, 11, 0.35)'
                                            : '1px solid transparent',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-1 text-800">
                                        <span style={{ width: 12, height: 2, borderTop: '2px dashed #f59e0b', display: 'inline-block' }} />
                                        <span>-- Mes antepasado ({etiquetaMesAntepasado})</span>
                                    </div>
                                    <span
                                        className="fw-bold ms-1"
                                        style={{
                                            color: visibleSeries.mesAntepasado ? '#f59e0b' : undefined
                                        }}
                                    >
                                        {ordenesAntepasado} órdenes
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Columna Derecha: Gráfica ECharts */}
                    <div className="col-12 col-md-7 col-lg-8 position-relative">
                        {cargando ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                                <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                <span className="text-muted small">Cargando gráfica...</span>
                            </div>
                        ) : (
                            <ReactEChartsCore
                                echarts={echarts}
                                option={chartOptions}
                                style={{ height: '210px', width: '100%' }}
                            />
                        )}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

DistribuidorGraficaCard.propTypes = {
    resumen: PropTypes.object,
    historico: PropTypes.array,
    cargando: PropTypes.bool,
    mesesRango: PropTypes.number,
    setMesesRango: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    onDateChange: PropTypes.func
};

export default DistribuidorGraficaCard;

